/**
 * TARA (Estonian eID) authentication contract — BR-56868.
 *
 * The FE never calls the customer-profile retailer API directly: that API sits
 * behind gateway auth (GET on the token endpoint → 405 POST-only, POST → 401
 * WWW-Authenticate: Bearer), and the BFF model requires the browser to receive
 * an opaque `tokens_ref`, not raw tokens. So the FE talks to the webshop-engine
 * proxy (VITE_APP_PROXY_TARGET), which authenticates to the retailer API and
 * swaps its raw `access_token` for a `tokens_ref` — exactly as `/auth/token`
 * already does for password login (see src/utils/auth.ts).
 *
 * These paths target the webshop-engine proxy (webshop-api-proxy), which
 * implements the TARA endpoints below by calling the customer-profile retailer
 * API with client credentials and swapping its raw `access_token` for a
 * `tokens_ref`. The proxy routes are new (BR-57366) — deploy the proxy before
 * this flow works end-to-end.
 *
 * Contract CONFIRMED with BE:
 *   - Login init: proxy → customer-profile grant_type=tara + return_uri, which
 *     returns a 302 whose Location is the TARA URL (BE validates return_uri and
 *     returns 400 invalid_return_uri if the host is not allowlisted).
 *   - Callback: BE redirects to return_uri with `login_result_token`.
 *   - No profile yet: tara-result returns 400 { error: "invalid_grant",
 *     error_description: "tara_customer_profile_not_found", registration_token }.
 */

/**
 * Proxy endpoint the browser navigates to (full page) to start TARA. The proxy
 * builds/forwards the TARA redirect; the FE only passes `return_uri`.
 */
export const TARA_LOGIN_PATH = '/auth/tara/login';

/** Proxy endpoint that exchanges the intermediate login-result token. */
export const TARA_RESULT_PATH = '/auth/tara/result';

export const TARA_REGISTER_PATH = '/auth/tara/register';

/** FE route TARA redirects back to; also the pre-registered `return_uri`. */
export const TARA_CALLBACK_ROUTE = '/auth/callback';

/**
 * Query param carrying the one-time login-result token on the callback URL
 * (BE redirects to return_uri with this).
 */
export const TARA_RESULT_TOKEN_PARAM = 'login_result_token';

/**
 * OAuth-style `error_description` the BE returns when the TARA-verified identity
 * has no customer profile yet — the signal to fork into registration.
 */
export const TARA_PROFILE_NOT_FOUND_ERROR = 'tara_customer_profile_not_found';
